// @ts-check

import { createLog } from '../utils/logger'
import { Broker } from '../utils/broker'
import { createModel } from './model'

const log = createLog()

const uiNamespace = '@mol-fe/mol-fe-cmp'

function loadScript(uiScriptBase, appState) {
  log('loadScript', appState)
  const script = (src) => {
    const script = document.createElement('script')
    script.dataset.state = JSON.stringify(appState)
    script.src = src
    script.id = 'mol-fe-cmp-script'
    return script
  }

  const src = uiScriptBase
  document.body.appendChild(script(src))
}

function uiHandlers(
  broker,
  clientBroker,
  cmpUpdateHandlers,
  removeStorageListener
) {
  const handler =
    (h) =>
      (...args) => {
        if (removeStorageListener) {
          removeStorageListener()
          removeStorageListener = null
        }
        h(...args)
      }

  broker.on(
    'close',
    handler((data) => {
      cmpUpdateHandlers.updateConsent(data)
    })
  )
  broker.on(
    'close:allow_all',
    handler(() => {
      cmpUpdateHandlers.consentToAll()
    })
  )
  broker.on(
    'close:deny_all',
    handler(() => {
      cmpUpdateHandlers.dissentToAll()
    })
  )

  broker.on('close:reject_all',
    handler(() => {
      cmpUpdateHandlers.updateConsent({})
    })
  )
  broker.on('close:allow_essential', cmpUpdateHandlers.dissentToAll)

  broker.on('level_changed', (data) => {
    log('level_changed', data)
    cmpUpdateHandlers.levelChanged(data)
    clientBroker.emit('level_changed', data)
  })
}

function handleExternalPreferenceUpdate(broker, reloadTcModel) {
  const handler = ({ key, newValue }) => {
    if (key !== 'mol.ads.cmp.tcf.client') return
    remove()
    const tcString = newValue
    try {
      reloadTcModel(tcString)
      broker.emit('close')
    } catch (error) {
      console.error(error)
    }
  }

  window.addEventListener('storage', handler)

  const remove = () => {
    window.removeEventListener('storage', handler)
  }
  return remove
}

export default class Ui {
  /**
   * @type {Promise<void>|undefined}
   */
  #pBannerIsReady

  /** @type {boolean} */
  #scriptLoaded

  /** @type {import('../config/config').Brand|undefined} */
  #brand

  /** @type {string} */
  #uiScriptUrl

  /** @type {'mobile'|'desktop'|undefined} */
  #renderPlatform

  /** @type {UiLevel|undefined} */
  #uiLevel

  /** @type {Broker} */
  #clientBroker

  /** @type {string} */
  #brokerId

  /** @type {Broker} */
  #broker

  /** @type {boolean} */
  #consentOrPay

  /** @type {boolean} */
  #isMailEssentialSubscriber

  /** @type {string|undefined} */
  #consentOrPaySubscriptionURL

  /** @type {boolean} */
  #isUserLoggedIn

  /** @type {boolean} */
  #allowRejectAll

  /** @type {string|undefined} */
  #tcfCacheKey

  /**
   * @param {UiOpts} opts
   */
  constructor({
    brand,
    uiScriptUrl,
    renderPlatform,
    uiLevel,
    clientBroker,
    consentOrPay,
    isMailEssentialSubscriber,
    consentOrPaySubscriptionURL,
    isUserLoggedIn,
    allowRejectAll,
    tcfCacheKey
  }) {
    this.#scriptLoaded = false
    this.#brand = brand
    this.#uiScriptUrl = uiScriptUrl
    this.#renderPlatform = renderPlatform
    this.#uiLevel = uiLevel
    this.#clientBroker = clientBroker
    this.#consentOrPay = !!consentOrPay
    this.#isMailEssentialSubscriber = !!isMailEssentialSubscriber
    this.#consentOrPaySubscriptionURL = consentOrPaySubscriptionURL
    this.#isUserLoggedIn = !!isUserLoggedIn
    this.#allowRejectAll = !!allowRejectAll
    this.#brokerId = Math.random().toString(36).substring(2)
    this.#broker = new Broker(
      uiNamespace,
      '@mol-ads/mol-ads-tcf',
      this.#brokerId
    )
    this.#pBannerIsReady = undefined
    this.#tcfCacheKey = tcfCacheKey
  }

  /**
   * @param {*} uiLevel
   * @param {{cmpModel: CmpModelForUi, cmpUpdateHandlers: CmpUpdateHandlers, isNewUser: boolean}} opts
   */
  open = (uiLevel, { cmpModel, cmpUpdateHandlers, isNewUser }) => {
    const model = createModel({
      cmpModel,
      currentLevel: this.#uiLevel,
      log,
      uiLevel,
      consentOrPay: this.#consentOrPay,
      isMailEssentialSubscriber: this.#isMailEssentialSubscriber,
      consentOrPaySubscriptionURL: this.#consentOrPaySubscriptionURL,
      isUserLoggedIn: this.#isUserLoggedIn,
      isNewUser,
      allowRejectAll: this.#allowRejectAll,
      tcfCacheKey: this.#tcfCacheKey
    })
    log('open', model)

    this.#pBannerIsReady = new Promise((resolve) => {
      this.#broker.once('is_ready', () => {
        resolve()
      })
    })

    if (!this.#scriptLoaded) {
      const removeStorageListener = handleExternalPreferenceUpdate(
        this.#broker,
        cmpUpdateHandlers.reloadTcModel
      )
      uiHandlers(
        this.#broker,
        this.#clientBroker,
        cmpUpdateHandlers,
        removeStorageListener
      )
      loadScript(this.#uiScriptUrl, {
        ...model,
        renderPlatform: this.#renderPlatform,
        brand: this.#brand,
        id: this.#brokerId
      })
      this.#scriptLoaded = true
    } else {
      this.#broker.emit('open', model)
    }
  }

  close = () => {
    log('closing UI')
    if (this.#pBannerIsReady) {
      this.#pBannerIsReady.then(() => {
        this.#broker.emit('close')
      })
    }
  }

  notifyUserPreferenceCaptured = () => {
    log('publishing user_consent_captured')
    this.#broker.emit('user_consent_captured')
  }
}

/**
 * @typedef {Object} CmpModelForUi
 * @property {import('@iabtechlabtcf/core').TCModel} tcModel
 * @property {boolean} hasUserSelectedTheirPreference
 * @property {import('../cmp/NonIABModel').UiModel} acmVendors
 * @property {import('../cmp/NonIABModel').UiModel} nonRegisteredVendors
 */

/**
 * @typedef {Record<string,()=>void>} CmpUpdateHandlers
 */

/**
 * @typedef {import('../config/config').UILevel} UiLevel
 */

/**
 * @typedef {Pick<import('../config/config').ClientConfig, 'brand'|'uiScriptUrl'|'renderPlatform'|'uiLevel'|'consentOrPay'|'isMailEssentialSubscriber'|'consentOrPaySubscriptionURL'|'isUserLoggedIn'|'allowRejectAll'|'tcfCacheKey'> & { clientBroker: Broker}} UiOpts
 */
